import { createQueryMatcherRetention } from '../utils/cache/query-matcher-retention.util';
import type { QueryClient } from '@tanstack/react-query';
import type {
  ConversationSearchRefreshState,
  ConversationSearchScope,
  ConversationSearchMatcher,
} from '../types/conversation-search-refresh.types';
import { scheduleBackgroundSearch } from './background-search.service';

const DOCUMENT_QUERY_KEY = ['crm/documents/funnel-conversations'];
const BATCH_DELAY_MS = 600;
const schedulers = new WeakMap<QueryClient, ConversationSearchRefreshState>();

/**
 * Coalesces realtime board recovery without cancelling an existing HTTP read.
 * Notifications during a read share one trailing refresh so its older response
 * cannot consume the only recovery opportunity. Calls before a batch starts
 * share that batch; each caller completes after its applicable refresh.
 *
 * @param queryClient - Owner of the board queries and transient scheduler.
 * @param scope - Exact affected query instances; omitted for broad lifecycle recovery.
 * @param matchesNewQuery - Domain predicate for filters opened during the batching delay.
 * @returns Completion of the applicable refresh, or cleanup of its owner.
 */
export const refreshConversationSearch = (
  queryClient: QueryClient,
  scope?: ConversationSearchScope,
  matchesNewQuery?: ConversationSearchMatcher
): Promise<void> => {
  const selected =
    scope ??
    new Set(
      queryClient.getQueryCache().findAll({ queryKey: DOCUMENT_QUERY_KEY })
    );
  // Preserve immediate staleness for inactive views without starting HTTP work.
  void queryClient.invalidateQueries({
    predicate: (query) => selected.has(query),
    refetchType: 'none',
  });
  if (
    ![...selected].some(
      (query) => query.isActive() || query.state.fetchStatus === 'fetching'
    ) &&
    !matchesNewQuery
  ) {
    return Promise.resolve();
  }

  let state = schedulers.get(queryClient);
  if (!state) {
    state = {
      stopped: false,
      unsubscribe: () => undefined,
      queries: new Set(),
      queryHashes: new Set(
        queryClient
          .getQueryCache()
          .findAll({ queryKey: DOCUMENT_QUERY_KEY })
          .map((query) => query.queryHash)
      ),
      newQueryMatchers: createQueryMatcherRetention(),
    };
    schedulers.set(queryClient, state);
  }
  const owner = state;
  selected.forEach((query) => owner.queries.add(query));
  if (scope === undefined) owner.newQueryMatchers.add(() => true);
  else if (matchesNewQuery) owner.newQueryMatchers.add(matchesNewQuery);
  // Re-read mutable lifecycle state after awaits; notifications/cleanup can
  // change it while this batch is suspended.
  const isStopped = (): boolean => owner.stopped;
  const hasPending = (): boolean => owner.pending !== undefined;
  const dispose = (): void => {
    owner.stopped = true;
    window.clearTimeout(owner.timer);
    owner.finishDelay?.();
    owner.unsubscribe();
    owner.newQueryMatchers.clear();
    if (schedulers.get(queryClient) === owner) schedulers.delete(queryClient);
  };
  if (owner.pending) return owner.pending;

  if (!owner.running) {
    owner.unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (
        event.type === 'removed' &&
        queryClient.getQueryCache().findAll({ queryKey: DOCUMENT_QUERY_KEY })
          .length === 0
      ) {
        dispose();
      }
    });
  }

  const pending = (owner.running ?? Promise.resolve()).then(async () => {
    if (isStopped()) return;
    await new Promise<void>((resolve) => {
      owner.finishDelay = resolve;
      owner.timer = window.setTimeout(resolve, BATCH_DELAY_MS);
    });
    owner.finishDelay = undefined;
    owner.timer = undefined;
    if (isStopped()) return;
    owner.pending = undefined;
    // Include filters opened during the delay, while retaining exact-instance
    // ownership for keys which already existed when recovery was requested.
    queryClient
      .getQueryCache()
      .findAll({ queryKey: DOCUMENT_QUERY_KEY })
      .forEach((query) => {
        if (
          !owner.queryHashes.has(query.queryHash) &&
          owner.newQueryMatchers.matches(query)
        ) {
          owner.queries.add(query);
        }
        owner.queryHashes.add(query.queryHash);
      });
    owner.newQueryMatchers.clear();
    const queries = [...owner.queries];
    owner.queries.clear();
    const running = Promise.resolve()
      .then(async () => {
        await Promise.all(
          queries.map(async (query) => {
            const isOwned = (): boolean =>
              !isStopped() &&
              queryClient.getQueryCache().get(query.queryHash) === query;
            const filters = {
              predicate: (candidate: typeof query) => candidate === query,
            };
            // Join pre-event work before queuing a new HTTP read. Success or
            // cancellation can consume the initial invalidation, including when
            // the view closes during the wait. Restore staleness for reopening.
            if (query.state.fetchStatus === 'fetching') {
              // QueryClient.refetchQueries skips disabled observers, even when
              // their older HTTP read still runs. Join that exact query instead.
              await query
                .fetch(undefined, { cancelRefetch: false })
                .catch(() => undefined);
            }
            if (!isOwned()) return;
            await queryClient.invalidateQueries({
              predicate: filters.predicate,
              refetchType: 'none',
            });
            return scheduleBackgroundSearch(queryClient, {
              isRelevant: () => isOwned() && query.isActive(),
              run: () =>
                queryClient.refetchQueries(
                  { ...filters, type: 'active' },
                  {
                    cancelRefetch: false,
                  }
                ),
            });
          })
        );
      })
      // React Query retains request errors; they must not block a trailing batch.
      .catch(() => undefined);
    owner.running = running;
    await running;
    owner.running = undefined;
    if (!hasPending()) dispose();
  });
  owner.pending = pending;
  return pending;
};
